import { User } from "src/graphql/types";

export type SetupWorkspace = "agent" | "booker";
type WorkspaceIntent = {
  workspace: SetupWorkspace;
  createdAt: number;
  userId?: string;
};

const KEY = "contact.workspaceSetup";
const MAX_AGE = 60 * 60 * 1000;
let fallback: WorkspaceIntent | null = null;

export const isSetupWorkspace = (value: unknown): value is SetupWorkspace =>
  value === "agent" || value === "booker";

export const clearWorkspaceIntent = () => {
  fallback = null;
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Private browsing can disable storage; the current-page fallback still works.
  }
  const url = new URL(window.location.href);
  if (url.searchParams.has("workspace")) {
    url.searchParams.delete("workspace");
    window.history.replaceState(window.history.state, "", url);
  }
};

const save = (intent: WorkspaceIntent) => {
  fallback = intent;
  try {
    sessionStorage.setItem(KEY, JSON.stringify(intent));
  } catch {
    // Keep only this page's intent if storage is unavailable.
  }
};

export const rememberWorkspaceIntent = (
  workspace: SetupWorkspace,
  userId?: string,
) => save({ workspace, userId, createdAt: Date.now() });

export const readWorkspaceIntent = (): WorkspaceIntent | null => {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    raw = fallback ? JSON.stringify(fallback) : null;
  }
  let value: WorkspaceIntent | null;
  try {
    value = JSON.parse(raw || "null");
  } catch {
    clearWorkspaceIntent();
    return null;
  }
  if (!value) return null;
  if (
    !isSetupWorkspace(value.workspace) ||
    typeof value.createdAt !== "number" ||
    !Number.isFinite(value.createdAt) ||
    value.createdAt > Date.now() ||
    Date.now() - value.createdAt >= MAX_AGE ||
    (value.userId !== undefined && typeof value.userId !== "string")
  ) {
    clearWorkspaceIntent();
    return null;
  }
  return value;
};

// Run before startup authentication. Reading first also removes expired URL hints.
export const restoreWorkspaceIntent = () => {
  const existing = readWorkspaceIntent();
  const requested = new URLSearchParams(window.location.search).get(
    "workspace",
  );
  if (isSetupWorkspace(requested) && existing?.workspace !== requested) {
    rememberWorkspaceIntent(requested);
  }
};

export const bindWorkspaceIntent = (userId: string) => {
  const intent = readWorkspaceIntent();
  if (!intent) return;
  if (intent.userId && intent.userId !== userId) {
    clearWorkspaceIntent();
  } else if (!intent.userId) {
    save({ ...intent, userId });
  }
};

export const workspaceSetupPath = (
  path: string,
  workspace = readWorkspaceIntent()?.workspace,
) =>
  workspace
    ? `${path}${path.includes("?") ? "&" : "?"}workspace=${workspace}`
    : path;

export const canSetUpAgency = (user: User | null) =>
  !!user?.kinds?.includes("booker") &&
  !user.kinds?.includes("agent") &&
  !user.kinds.includes("talent");

export const workspaceSetupDestination = (
  user: User,
  workspace: SetupWorkspace,
) => {
  if (user.kinds?.includes(workspace)) {
    return workspace === "agent"
      ? "/dashboard/agent/talent"
      : "/dashboard/client";
  }
  if (!user.kinds?.length) return "/account/onboarding";
  if (
    workspace === "booker" &&
    user.kinds?.includes("agent") &&
    user.agency?.representation === "third_party_represented"
  ) {
    return "/jobs/new/shortlist";
  }
  return workspaceSetupPath("/account/add-workspace", workspace);
};

export const isDuplicateEmailError = (error: unknown) => {
  const value = error as {
    message?: string;
    graphQLErrors?: { extensions?: { code?: string } }[];
  } | null;
  return (
    value?.message === "Email has already been taken" ||
    !!value?.graphQLErrors?.some(
      (item) => item.extensions?.code === "EMAIL_ALREADY_EXISTS",
    )
  );
};
